<template>
  <div :class="['ui-input', classList]">
    <label v-if="label" class="ui-input__label" :for="id">
      {{ label }}
    </label>
    <div class="ui-input__wrp">
      <transition name="fade">
        <div v-if="$slots.prepend" class="ui-input-prepend">
          <slot name="prepend" />
        </div>
      </transition>
      <input
        :id="id"
        ref="input"
        :name="name"
        class="ui-input__native"
        v-bind="$attrs"
        :autocomplete="autocomplete"
        :value="computedValue"
        :disabled="disabled"
        :readonly="readonly"
        @input="update(($event.target as HTMLInputElement).value)"
        @keydown="onKeyDown"
        @focus="onFocus"
        @blur="onBlur"
      >
      <transition name="fade">
        <ui-loader v-if="loading" />
        <button
          v-else-if="showClear"
          tabindex="-1"
          class="ui-input-append"
          type="button"
          @mousedown.left="onClearDown"
          @mouseup.left="onClearUp"
        >
          <f-sprite-icon :icon-link="$options.icons.IconClose" />
        </button>
        <div
          v-else-if="$slots.append"
          class="ui-input-append"
        >
          <slot name="append" />
        </div>
      </transition>
    </div>

    <p v-if="$slots.error" class="error">
      <slot name="error" />
    </p>

    <p v-else-if="computedError" class="error">
      {{ computedError }}
    </p>
    <slot name="after" />
  </div>
</template>

<script lang="ts">
import { type PropType, defineComponent } from 'vue';

import IconClose from '@icons/close.svg';

import validators, { TRule, TValidatorKey } from './validators';

export default defineComponent({
  name: 'ui-input',

  icons: {
    IconClose,
  },

  props: {
    modelValue: {
      type: [String, Number],
      default: '',
    },

    label: {
      type: String,
      default: '',
    },

    mask: {
      type: String,
      default: '',
    },

    error: {
      type: [String, Boolean],
      default: '',
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    autofocus: {
      type: Boolean,
      default: false,
    },

    autocomplete: {
      type: String as PropType<'on' | 'off'>,
      default: 'on',
    },

    clearable: {
      type: Boolean,
      default: false,
    },

    large: {
      type: Boolean,
      default: false,
    },

    validate: {
      type: [Array, String] as PropType<TValidatorKey | TValidatorKey[]>,
      default: () => ([]),
    },

    numbers: {
      type: Boolean,
      default: false,
    },

    name: {
      type: String,
      default: '',
    },
  },

  emits: [
    'update:modelValue',
    'is-valid',
    'on-valid',
    'mask-completed',
    'focus',
    'blur',
  ],

  data: () => ({
    focused: false,
    validationError: '',
    lastKeyCode: null as number | null,
    forceRecompute: 0,
    isClearProcess: false,
    blurTimeout: null,
  }),

  computed: {
    id() {
      return `ui-input-${this.label || 'label'}-${this.mask || 'mask'}-${this.name || 'name'}`;
    },

    inputElement(): HTMLInputElement | null {
      return this.$refs.input as HTMLInputElement || null;
    },

    classList() {
      return [
        {
          error: !!this.computedError,
          large: this.large,
          disabled: this.disabled,
          readonly: this.readonly,
          focused: this.focused && !this.readonly,
          code: this.mask === 'code',
          'ui-input_append': this.showClear || this.$slots.append,
          'ui-input_prepend': this.$slots.prepend,
        },
      ];
    },

    validators(): TRule[] {
      if (typeof this.validate === 'string') {
        return validators[this.validate] || [];
      }
      return this.validate
        .map((r) => validators[r])
        .filter((i) => !!i)
        .flat();
    },

    computedError(): string {
      if (this.loading || this.valid) {
        return '';
      }
      if (typeof this.error === 'string' && this.error) {
        return this.error;
      }
      return this.validationError;
    },

    currentMask() {
      switch (this.mask) {
        case 'phone': return '+7 (9##) ###-##-##';
        case 'date': return '##.##.####';
        case 'interval': return '##.##.#### - ##.##.####';
        case 'code': return '######';
        case 'codeCall': return '####';
        default: return this.mask.includes('#') ? this.mask : '';
      }
    },

    computedValue(): string {
      return this.currentMask && this.forceRecompute >= 0 && this.modelValue
        ? this.$filters.mask(`${this.modelValue}`, this.currentMask) // маскируем только при маске
        : `${this.modelValue}`; // без маски
    },

    showClear() {
      return this.clearable && this.modelValue && this.focused;
    },

    valid() {
      // цепляемся по рефу сверху сюда
      // например для дизейбла кнопки сабмита
      return !this.checkValid();
    },
  },

  mounted() {
    if (this.autofocus) {
      this.focus();
    }
  },

  methods: {
    onKeyDown(e: KeyboardEvent) {
      if (!this.currentMask) {
        return;
      }
      this.lastKeyCode = e.keyCode;
      if (e.keyCode === 8) {
        e.preventDefault();
        this.onBackSpace();
      }
    },

    // prevented. удаляем руками для правильной обработки курсора (всгда --)
    onBackSpace() {
      const i = this.inputElement?.selectionStart || 0;

      if (i === 0 && this.inputElement?.selectionEnd === this.computedValue.length) {
        return this.update('');
      }

      if (i > 0) {
        this.update(this.computedValue.slice(0, i - 1) + this.computedValue.slice(i));
      }
    },

    update(v: string) {
      let value = v;

      if (this.numbers && this.inputElement) {
        value = value.replace(/[\D]/g, '');
        this.inputElement.value = value;
      }
      if (this.currentMask) {
        if (this.mask === 'phone' && value?.[0] === '8') {
          value = `+7${value.slice(1)}`;
        }
        value = this.$filters.mask(value, this.currentMask);
        this.fixMaskedCursor(this.modelValue !== value);
        value = this.outputValue(value);
      }

      // принудительно пересчитываем инпут, иначе не видит обновления на инпуте
      // и не обновляется v-model, остаются незамаскированные символы
      if (value === this.modelValue) {
        this.forceRecompute += 1;
        return;
      }

      this.validationError = '';
      this.$emit('update:modelValue', value);

      this.$nextTick(() => {
        this.$emit('is-valid', this.valid); // возвращает статус валидности по каждому нажатию
        // отрабатывает, когда введенное валидно
        if (this.valid) {
          this.$emit('on-valid');
        }

        if (value?.length === this.currentMask?.length) {
          this.$emit('mask-completed', value);
        }
      });
    },
    outputValue(v: string) {
      return this.mask === 'phone' ? v.replace(/\D/g, '') : v;
    },

    fixMaskedCursor(valueChanged = true) {
      let index = this.inputElement?.selectionStart || 0;
      switch (this.lastKeyCode) {
        case 8: // backspace
          index -= 1;
          break;
        case 46: // delete
          if (!valueChanged) {
            index += 1;
          }
          break;
        default:
          if (valueChanged) {
            index += this.currentMask.slice(index - 1).indexOf('#');
          } else {
            index -= 1;
          }
          break;
      }
      // тут 2 nextTick, чтобы получить и обновить значение из v-model
      this.$nextTick(() => this.setCursorPosition(index));
    },

    setCursorPosition(pos: number) {
      this.$nextTick(() => this.inputElement?.setSelectionRange(pos, pos));
    },

    // для внешннего вызова
    checkValidOutside() {
      this.validationError = this.errorMessage();
      return this.valid;
    },

    resetValid() {
      if (this.validationError) this.validationError = '';
    },

    errorMessage(): string {
      const msg = this.checkValid();
      if (typeof msg === 'string') {
        return msg;
      }
      return '';
    },

    checkValid(): string | boolean {
      if (this.error) {
        return true;
      }

      if (!this.validators.length) {
        return false;
      }

      let error = '';
      this.validators.some((rule) => {
        if (typeof rule === 'function') {
          const errMsg = rule(this.computedValue);
          if (typeof errMsg === 'string') {
            error = errMsg;
          }
        } else {
          const [regex, err] = rule;
          if (!(new RegExp(regex)).test(this.computedValue)) {
            error = err;
          }
        }
        return !!error;
      });

      return error;
    },

    focus() {
      this.inputElement?.focus();
    },

    blur() {
      this.inputElement?.blur();
    },

    onClearDown() {
      this.isClearProcess = true;
    },

    onClearUp() {
      this.update('');
      this.focus();
      this.isClearProcess = false;
    },

    onFocus() {
      document.removeEventListener('keyup', this.blurComplete);
      document.removeEventListener('mouseup', this.blurComplete);

      this.focused = true;
      this.$nextTick(() => {
        this.validationError = '';
      });
      this.$emit('focus');
    },

    onBlur() {
      if (this.isClearProcess) {
        return;
      }
      /**
       * Это необходимо для валидации после фактического блюра
       * Необходимо это для случаев когда есть какое-то действие под полем ввода
       * Без таймаута ошибка сразу после блюра двигает эту ссылку/кнопку вниз и не происходит клика на этот элемент
       * Пример окно авторизации -> поле ввода кода -> повторный запрос кода
       */
      if (this.validators.length) {
        document.addEventListener('keyup', this.blurComplete, { once: true });
        document.addEventListener('mouseup', this.blurComplete, { once: true });
      } else {
        this.blurComplete();
      }
    },

    blurComplete() {
      this.validationError = this.errorMessage();
      this.focused = false;
      this.$emit('blur', `${this.modelValue}`.length);
    },
  },
});
</script>

<style lang="scss">
.ui-input {
  position: relative;
  min-height: 84px; // 64 + запас на ошибку 20

  &__label,
  p.error {
    display: block;
    font-size: 12px;
    line-height: 20px;
  }

  &__label {
    margin-bottom: 4px;
    color: $gray;
  }

  p.error {
    margin-top: 4px;
    color: $red;
  }

  &__wrp {
    position: relative;
    width: 100%;
    height: 40px;
    padding: 1px 12px;
    border: 1px solid $gray_lu;
    border-radius: 4px;
    overflow: hidden;
    transition: .2s ease border-color;
    display: flex;
    align-items: center;
    background: $white;
  }

  &.error:not(.disabled) .ui-input__wrp {
    border-color: $red !important;
  }

  &.large {
    .ui-input__wrp {
      height: 56px;

      & > .ui-input__native {
        font-size: 16px;
        line-height: 24px;
      }
    }
    .sprite-icon {
      width: 24px;
      height: 24px;
    }
  }

  &.readonly,
  &.disabled {
    .ui-input__wrp {
      background: $gray_bgr;
    }
    .ui-input__native {
      background-color: $gray_bgr;
      box-shadow: 0 0 0 1000px $gray_bgr inset;
    }
  }

  &.disabled {
    pointer-events: none;

    p.error {
      display: none;
    }

    .ui-input__native {
      color: $gray_l;
    }
  }

  &.readonly {
    pointer-events: auto;

    .ui-input-append svg {
      color: $black;
    }
  }

  &.focused {
    .ui-input__wrp {
      border-color: $black;
    }
  }

  &.code {
    .ui-input__native {
      letter-spacing: 4px;
    }
  }

  &_append {
    .ui-input__native {
      padding-right: 12px;
    }
  }

  &_prepend {
    .ui-input__native {
      padding-left: 12px;
    }
  }

  &__native {
    flex: 1;
    width: 100%;
    height: 100%;
    padding: 0;
    font-size: 14px;
    line-height: 20px;
    color: $black;
    border: none;
    outline: none;
    box-shadow: 0 0 0 1000px $white inset;
    /* stylelint-disable-next-line */
    &::-webkit-input-placeholder {
      color: $gray;
      line-height: 20px;
      letter-spacing: normal;
      transition: .2s color ease;
      &:focus {
        color: transparent;
      }
    }
  }

  &-append,
  &-prepend {
    display: flex;
    align-items: center;
    height: 100%;
  }

  &-append {
    cursor: pointer;

    svg.sprite-icon {
      width: 16px;
      height: 16px;
      color: $gray_l;
    }
  }

  .ui-loader {
    color: $gray;
  }
}
@include breakpoint("desktop") {
  .ui-input {

    .ui-input__wrp:hover {
      border-color: $gray_l;
    }

    &.focused {
      .ui-input__wrp:hover {
        border-color: $black;
      }
    }

    &.readonly {
      .ui-input__wrp:hover {
        border-color: $gray_lu;
      }
    }
  }
}
</style>
